import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { useSupportStore } from "../../../store/useSupportStore";
import { useModelStore } from "../../../store/useModelStore";

import { Trash2, Eye, EyeOff, Plus } from "lucide-react";
import {
  ArrowUpOnSquareIcon,
  ArrowDownOnSquareIcon,
  ClipboardDocumentIcon,
} from "@heroicons/react/24/solid";
import {
  importSupportsFromCSV,
  exportSupportsToCSV,
  copySupportsToClipboard,
  processExcelFile,
  createFileValidationError,
  generateFilename,
  transformSupportCSVData,
} from "@/utilities/CSVUtils";

import {
  SupportPosition,
  SupportType,
  DegreesOfFreedom,
  DEFAULT_DOF_CONFIGS,
  SUPPORT_VISUAL_CONFIGS,
} from "../../../types/support";

import { SupportConfigDialog } from "../../ui/SupportConfigDialog";

import {
  TableLayout,
  TableColumn,
} from "../../workspace/components/ui/TableLayout";
import ManualLoadInput from "../../ui/ManualLoadInput";
import { useWorkspaceState } from "../../../../src/components/workspace/hooks/core/useWorkspaceState";
import { useCursorManager } from "../../menu/hooks/useCursorManager";
import {
  useTableSelection,
  useCursorMode,
  useCancelAllApplyModes,
} from "@/hooks/useGlobalSelection";
import { useGlobalSelectionStore } from "@/store/useGlobalSelectionStore";
import {
  validateSupportNodeReferences,
  validateDuplicateSupportAssignments,
} from "@/utils/structuralValidation";

const SUPPORT_POSITION_LABELS: Record<string, string> = {
  below: "Below Node (-Z Down)",
  above: "Above Node (+Z Up)",
  left: "Left of Node (-X West)",
  right: "Right of Node (+X East)",
  front: "Front of Node (+Y North)",
  back: "Back of Node (-Y South)",
};

export const SupportsDataTable: React.FC = () => {
  const {
    supports,
    removeSupport,
    isVisible,
    toggleVisible,
    addSupport,
    updateSupport,
    setSupports,
  } = useSupportStore();

  const { nodes, selectedNodeIds } = useModelStore();

  const { callbacks } = useWorkspaceState();
  const { setCursorByState } = useCursorManager();

  const [filterType, setFilterType] = useState<
    "all" | "fixed" | "pinned" | "roller"
  >("all");
  const [supportDialogVisible, setSupportDialogVisible] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Assignment Modes
  const [applyByClickMode, setApplyByClickMode] = useState(false);
  const [applyToSelectedMode, setApplyToSelectedMode] = useState(false);
  // const [selectedSupport, setSelectedSupport] = useState<any>(null);

  const { selectRow, unselectRow, isRowSelected, activeRowData } =
    useTableSelection("supports");
  const { setCursorMode, clearCursorMode } = useCursorMode();
  const globalActiveTable = useGlobalSelectionStore(
    (state) => state.activeTable
  );
  const globalActiveRowId = useGlobalSelectionStore(
    (state) => state.activeRowId
  );
  const prevActiveTable = useRef<string | null>(null);

  const selectedSupport = activeRowData;

  const cancelAllModes = useCancelAllApplyModes();
  const [manualPopupOpen, setManualPopupOpen] = useState(false);

  const [activationMessage, setActivationMessage] = useState<string | null>(
    null
  );

  // Inline CSV editing state for Node(s) column
  const [editingNodeIds, setEditingNodeIds] = useState<{
    supportId: string;
    value: string;
  } | null>(null);

  console.log("supports", supports);

  // Start editing node IDs
  const startEditingNodes = useCallback(
    (e: React.MouseEvent, supportId: string, currentNodeIds: string[]) => {
      e.stopPropagation();
      setEditingNodeIds({
        supportId,
        value: currentNodeIds.join(", "),
      });
    },
    []
  );

  /**
   * Check if any node already has a support assigned
   * Returns array of nodes that already have supports
   */
  const getNodesWithExistingSupports = useCallback(
    (
      nodeIds: string[],
      currentSupportId: string,
      allSupports: any[]
    ): string[] => {
      // Use centralized validation
      const errors = validateDuplicateSupportAssignments(
        allSupports.map((s) => ({
          nodeIds: s.nodeIds || [],
        }))
      );

      // Convert validation errors to node IDs that have conflicts
      const nodesWithSupports: string[] = [];

      nodeIds.forEach((nodeId) => {
        // Check if this node exists in multiple supports
        const supportsWithThisNode = allSupports.filter(
          (s) => s.id !== currentSupportId && s.nodeIds.includes(nodeId)
        );

        if (supportsWithThisNode.length > 0) {
          nodesWithSupports.push(nodeId);
        }
      });

      return nodesWithSupports;
    },
    []
  );

  // Commit edited node IDs
  const commitNodeIdsEdit = useCallback(() => {
    if (!editingNodeIds) return;

    const input = editingNodeIds.value.trim();
    const nodeIdsArray = Array.from(
      new Set(
        input
          .split(",")
          .map((id) => id.trim())
          .filter((id) => id !== "")
      )
    );

    if (nodeIdsArray.length === 0) {
      updateSupport(editingNodeIds.supportId, { nodeIds: [] });
      setEditingNodeIds(null);
      return;
    }

    // Validation: Check all are numeric
    const nonNumeric = nodeIdsArray.filter((id) => !/^\d+$/.test(id));
    if (nonNumeric.length > 0) {
      alert(
        `Invalid node IDs (must be numeric only):\n${nonNumeric.join(", ")}`
      );
      setEditingNodeIds(null);
      return;
    }

    // 1. Validate node references exist
    const validationErrors = validateSupportNodeReferences(
      [{ nodeIds: nodeIdsArray }],
      nodes.map((n) => n.id)
    );

    if (validationErrors.length > 0) {
      const missingNodes = nodeIdsArray.filter(
        (id) => !nodes.find((n) => n.id === id)
      );
      alert(
        `The following node IDs do not exist in the node table:\n${missingNodes.join(", ")}`
      );
      setEditingNodeIds(null);
      return;
    }

    // 2. Check for duplicate support assignments
    const support = supports.find((s) => s.id === editingNodeIds.supportId);
    if (!support) {
      setEditingNodeIds(null);
      return;
    }

    const conflictingNodes = getNodesWithExistingSupports(
      nodeIdsArray,
      support.id,
      supports
    );

    if (conflictingNodes.length > 0) {
      alert(
        `Cannot assign support - the following nodes already have supports:\n${conflictingNodes.join(", ")}`
      );
      setEditingNodeIds(null);
      return;
    }

    // Update support
    updateSupport(support.id, { nodeIds: nodeIdsArray });
    setEditingNodeIds(null);
  }, [
    editingNodeIds,
    nodes,
    supports,
    updateSupport,
    getNodesWithExistingSupports,
  ]);

  // Helper function to click toolbar buttons
  const clickToolbarButton = useCallback((searchTexts: string[]) => {
    const buttons = document.querySelectorAll("button");
    for (const text of searchTexts) {
      const button = Array.from(buttons).find((btn) => {
        const btnText = btn.textContent?.trim();
        const isModelingButton = btn.closest('[class*="bg-gray-8"]') !== null;
        if (!isModelingButton) return false;
        return btnText === text || btnText?.includes(text);
      });
      if (button && button.getAttribute("aria-pressed") === "true") {
        button.click();
        return true;
      }
    }
    return false;
  }, []);

  // Helper function to disable all modeling modes
  const disableAllModes = useCallback(() => {
    const modelStore = useModelStore.getState();

    clickToolbarButton(["Create Node", "Node"]);
    clickToolbarButton(["Create Beam", "Beam"]);
    clickToolbarButton(["Prep Mode"]);
    clickToolbarButton(["Divide Beam"]);

    // Cancel states in callbacks
    if (callbacks?.setIsNodeCreationMode) {
      callbacks.setIsNodeCreationMode(false);
    }

    if (callbacks?.setIsBeamCreationMode) {
      callbacks.setIsBeamCreationMode(false);
    }

    // Cancel prep mode
    if (modelStore.isPrepMode) {
      modelStore.cancelPrepMode();
    }

    // Cancel divide beam mode
    if (modelStore.isDivideBeamMode) modelStore.cancelDivideBeamMode();

    // Cancel beam creation
    if (modelStore.cancelBeamCreation) {
      modelStore.cancelBeamCreation();
    }

    // Reset cursor
    clearCursorMode();
  }, [callbacks, clearCursorMode, clickToolbarButton]);

  // Toggle support selection by clicking a row
  const handleRowToggle = useCallback(
    (support: any) => {
      const isAlreadySelected = isRowSelected(support.id);

      if (isAlreadySelected) {
        unselectRow();
        setApplyByClickMode(false);
        setApplyToSelectedMode(false);
        clearCursorMode();
        setActivationMessage("Support deselected.");
      } else {
        cancelAllModes();
        disableAllModes();
        selectRow(support.id, support);
        setActivationMessage(
          `Support selected: ${support.type} ${support.position}. Choose Apply Mode.`
        );
      }

      setTimeout(() => setActivationMessage(null), 1000);
    },
    [
      cancelAllModes,
      disableAllModes,
      isRowSelected,
      unselectRow,
      selectRow,
      clearCursorMode,
    ]
  );

  const filteredAndIndexed = useMemo(() => {
    const filtered =
      filterType === "all"
        ? supports
        : supports.filter((s) => s.type === filterType);

    return filtered.map((support, idx) => ({
      ...support,
      supportId: idx + 1,
    }));
  }, [supports, filterType]);

  console.log("table filter data", filteredAndIndexed);

  const getNodePosition = (support: any) => {
    if (!support.nodeIds.length) {
      return SUPPORT_POSITION_LABELS[support.position];
    }

    const node = nodes.find((n) => n.id === support.nodeIds[0]);
    if (!node) return SUPPORT_POSITION_LABELS[support.position];

    return `(${node.x.toFixed(2)}, ${node.y.toFixed(2)}, ${node.z.toFixed(2)})`;
  };

  const handleDelete = (row: any) => {
    const nodeCount = row.nodeIds?.length || 0;
    const confirmMsg =
      nodeCount > 0
        ? `Delete this support from ${nodeCount} node(s)?`
        : "Delete this support?";

    if (confirm(confirmMsg)) {
      // Delete all supports in this group
      if (row.supportIds && row.supportIds.length > 0) {
        row.supportIds.forEach((id: string) => removeSupport(id));
      } else {
        removeSupport(row.id);
      }

      unselectRow();
      setApplyByClickMode(false);
      setApplyToSelectedMode(false);
      clearCursorMode();

      // Show confirmation message
      setActivationMessage(`Deleted support from ${nodeCount} node(s)`);
      setTimeout(() => setActivationMessage(null), 2000);
    }
  };

  const handleSupportConfirm = (
    nodeIds: string[],
    position: SupportPosition,
    type: SupportType,
    dof?: DegreesOfFreedom
  ) => {
    const finalDOF = dof || DEFAULT_DOF_CONFIGS[type];
    const visualConfig = SUPPORT_VISUAL_CONFIGS[type];

    // Always create ONE support
    const created = addSupport(nodeIds, position, type);

    updateSupport(created.id, {
      dof: finalDOF,
      color: SUPPORT_VISUAL_CONFIGS[type].color,
    });

    setSupportDialogVisible(false);
  };

  const assignSupportToNode = (nodeId: string, supportId: string) => {
    const store = useSupportStore.getState();
    const freshSupport = store.getSupportById(supportId);
    if (!freshSupport) return false;

    // Prevent duplicates within same support
    if (freshSupport.nodeIds.includes(nodeId)) {
      setActivationMessage(
        `Already applied ${freshSupport.type} ${freshSupport.position} to Node ${nodeId}`
      );
      setTimeout(() => setActivationMessage(null), 2000);
      return false;
    }

    // CHECK if node already has ANOTHER support
    const nodesWithSupports = getNodesWithExistingSupports(
      [nodeId],
      supportId,
      supports
    );

    if (nodesWithSupports.length > 0) {
      alert(
        `Node ${nodeId} already has a support assigned!\n\n` +
          `Each node can only have ONE support.\n\n` +
          `Remove the existing support first, then assign a new one.`
      );
      return false;
    }

    updateSupport(freshSupport.id, {
      nodeIds: [...freshSupport.nodeIds, nodeId],
      color: SUPPORT_VISUAL_CONFIGS[freshSupport.type].color,
    });
    return true;
  };

  // Auto-deselect when switching to another table
  useEffect(() => {
    if (
      prevActiveTable.current === "supports" &&
      globalActiveTable !== "supports" &&
      globalActiveTable !== null
    ) {
      unselectRow();
      setApplyByClickMode(false);
      setApplyToSelectedMode(false);
      clearCursorMode();
      setActivationMessage(null);
    }

    prevActiveTable.current = globalActiveTable;
  }, [globalActiveTable]);

  useEffect(() => {
    const handleCancelSupports = () => {
      setApplyByClickMode(false);
      setApplyToSelectedMode(false);
      unselectRow();
      clearCursorMode();
      setActivationMessage(null);
    };
    window.addEventListener("cancelSupportsApplyModes", handleCancelSupports);
    return () =>
      window.removeEventListener(
        "cancelSupportsApplyModes",
        handleCancelSupports
      );
  }, [unselectRow, clearCursorMode]);

  useEffect(() => {
    if (!applyByClickMode || !selectedSupport) return;

    const handleApplyByClickNode = (event: CustomEvent) => {
      const { nodeId } = event.detail;
      const success = assignSupportToNode(nodeId, selectedSupport.id);

      if (success) {
        setActivationMessage(
          `Applied ${selectedSupport.type} ${selectedSupport.position} to Node ${nodeId}`
        );
        setTimeout(() => setActivationMessage(null), 2000);
      }
    };

    window.addEventListener(
      "applyByClickNode",
      handleApplyByClickNode as EventListener
    );

    return () => {
      window.removeEventListener(
        "applyByClickNode",
        handleApplyByClickNode as EventListener
      );
    };
  }, [applyByClickMode, selectedSupport, assignSupportToNode]);

  const cancelApplyByClick = useCallback(() => {
    setApplyByClickMode(false);
    clearCursorMode();
    unselectRow();
    setActivationMessage("Apply by Click canceled.");
    setTimeout(() => {
      setActivationMessage(null);
    }, 1000);
  }, [clearCursorMode, unselectRow]);

  // ESC Cancels Click Mode
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape" && applyByClickMode) {
        e.preventDefault();
        e.stopPropagation();
        cancelApplyByClick();
      }
    };

    window.addEventListener("keydown", handleEsc, true);
    return () => window.removeEventListener("keydown", handleEsc, true);
  }, [applyByClickMode, cancelApplyByClick]);

  const handleApplyToSelectedConfirm = () => {
    if (!selectedSupport || selectedNodeIds.length === 0) return;
    const store = useSupportStore.getState();
    const freshSupport = store.getSupportById(selectedSupport.id);
    if (!freshSupport) return;

    const validationErrors = validateSupportNodeReferences(
      [{ nodeIds: selectedNodeIds }],
      nodes.map((n) => n.id)
    );

    if (validationErrors.length > 0) {
      const missingNodes = selectedNodeIds.filter(
        (nodeId) => !nodes.find((n) => String(n.id) === String(nodeId))
      );

      alert(
        `Cannot apply support - ${missingNodes.length} selected node(s) do not exist:\n\n` +
          `${missingNodes.join(", ")}\n\n` +
          `Please create them first, then try again.`
      );
      return;
    }

    // CHECK if any selected node already has support
    const nodesWithSupports = getNodesWithExistingSupports(
      selectedNodeIds,
      freshSupport.id,
      supports
    );

    if (nodesWithSupports.length > 0) {
      alert(
        `Cannot assign support to ${nodesWithSupports.length} node(s):\n\n` +
          `${nodesWithSupports.join(", ")}\n\n` +
          `These nodes already have supports assigned.\n` +
          `Each node can only have ONE support.`
      );
      return;
    }

    const mergedNodeIds = Array.from(
      new Set([...freshSupport.nodeIds, ...selectedNodeIds])
    );

    updateSupport(freshSupport.id, {
      nodeIds: mergedNodeIds,
      color: SUPPORT_VISUAL_CONFIGS[freshSupport.type].color,
    });

    setApplyToSelectedMode(false);
    unselectRow();
    clearCursorMode();
    setActivationMessage(
      `Applied ${freshSupport.type} (${freshSupport.position}) to ${selectedNodeIds.length} node(s)`
    );
    setTimeout(() => setActivationMessage(null), 1000);
  };

  const applyManualSupport = (nodeIds: string[]) => {
    if (!selectedSupport || nodeIds.length === 0) return;
    const store = useSupportStore.getState();
    const freshSupport = store.getSupportById(selectedSupport.id);
    if (!freshSupport) return;

    // Calculate what to add and what to remove
    const currentNodeIds = freshSupport.nodeIds || [];

    // Find nodes to ADD (new ones not currently assigned)
    const nodesToAdd = nodeIds.filter((id) => !currentNodeIds.includes(id));

    // Find nodes to REMOVE (currently assigned but not in new list)
    const nodesToRemove = currentNodeIds.filter((id) => !nodeIds.includes(id));

    console.log("🔄 Manual Input - Changes:", {
      toAdd: nodesToAdd,
      toRemove: nodesToRemove,
      final: nodeIds,
    });

    if (nodesToAdd.length > 0) {
      const validationErrors = validateSupportNodeReferences(
        [{ nodeIds: nodesToAdd }],
        nodes.map((n) => n.id)
      );

      if (validationErrors.length > 0) {
        const missingNodes = nodesToAdd.filter(
          (nodeId) => !nodes.find((n) => String(n.id) === String(nodeId))
        );

        alert(
          `Cannot apply support to ${missingNodes.length} node(s):\n\n` +
            `${missingNodes.join(", ")}\n\n` +
            `These nodes do not exist in the model.\n` +
            `Please create them first, then try again.`
        );
        return;
      }
    }

    // CHECK if any NEW node already has support from ANOTHER support
    if (nodesToAdd.length > 0) {
      const nodesWithSupports = getNodesWithExistingSupports(
        nodesToAdd,
        freshSupport.id,
        supports
      );

      if (nodesWithSupports.length > 0) {
        alert(
          `Cannot assign support to ${nodesWithSupports.length} node(s):\n\n` +
            `${nodesWithSupports.join(", ")}\n\n` +
            `These nodes already have supports assigned.\n` +
            `Each node can only have ONE support.\n\n` +
            `Remove existing supports first, then try again.`
        );
        return;
      }
    }

    // Apply the final list (this replaces the old nodeIds array)
    updateSupport(freshSupport.id, {
      nodeIds: nodeIds, // Use the complete new list
      dof: freshSupport.dof,
      color: SUPPORT_VISUAL_CONFIGS[freshSupport.type].color,
    });

    // Show detailed feedback
    const addedCount = nodesToAdd.length;
    const removedCount = nodesToRemove.length;

    const message =
      addedCount > 0 && removedCount > 0
        ? `✅ Updated Support: Added ${addedCount}, Removed ${removedCount} node(s)`
        : addedCount > 0
          ? `✅ Added ${addedCount} node(s) to ${freshSupport.type} (${freshSupport.position})`
          : removedCount > 0
            ? `✅ Removed ${removedCount} node(s) from ${freshSupport.type} (${freshSupport.position})`
            : `✅ Support unchanged (${nodeIds.length} node(s))`;

    setActivationMessage(message);
    setTimeout(() => setActivationMessage(null), 3000);

    setManualPopupOpen(false);
    unselectRow();
    clearCursorMode();
  };

  // Import/Export/Copy handlers
  const handleExport = useCallback(() => {
    const filename = generateFilename("supports");

    // Expand each support into multiple rows (one per node)
    const expandedSupports = supports.flatMap((support) => {
      // If support has nodeIds array, create one row per node
      if (Array.isArray(support.nodeIds) && support.nodeIds.length > 0) {
        return support.nodeIds.map((nodeId) => ({
          ...support,
          nodeId: nodeId, // CSV format: singular
          nodeIds: undefined, // Remove array field
        }));
      }

      // If support has old singular nodeId, keep as-is
      const legacySupport = support as any;
      if (legacySupport.nodeId) {
        return [
          {
            ...support,
            nodeId: legacySupport.nodeId,
            nodeIds: undefined,
          },
        ];
      }

      // Support has no nodes - export with empty nodeId
      return [
        {
          ...support,
          nodeId: "",
          nodeIds: undefined,
        },
      ];
    });

    // Export individual support records, not grouped
    exportSupportsToCSV(expandedSupports, { filename });
  }, [supports]);

  const handleFileSelected = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      const fileExtension = file.name.split(".").pop()?.toLowerCase();

      try {
        let result;
        if (fileExtension === "csv") {
          result = await importSupportsFromCSV(file);
        } else if (fileExtension === "xlsx" || fileExtension === "xls") {
          const excelResult = await processExcelFile(file);
          if (excelResult.success) {
            const transformedData = transformSupportCSVData(excelResult.data);
            result = {
              ...excelResult,
              data: transformedData,
              preview: transformedData.slice(0, 5),
            };
          } else {
            result = excelResult;
          }
        } else {
          alert(createFileValidationError(file, ["csv", "xlsx", "xls"]));
          return;
        }

        if (result.success) {
          const validSupports: any[] = [];
          const invalidSupports: string[] = [];
          const missingNodeIds = new Set<string>();

          result.data.forEach((support: any, index: number) => {
            const rowNum = index + 1;

            if (!support.type || typeof support.type !== "string") {
              invalidSupports.push(
                `Row ${rowNum}: Missing or invalid 'type' field`
              );
              return;
            }

            if (!support.position || typeof support.position !== "string") {
              invalidSupports.push(
                `Row ${rowNum}: Missing or invalid 'position' field`
              );
              return;
            }

            const validTypes: SupportType[] = ["fixed", "pinned", "roller"];
            if (!validTypes.includes(support.type as SupportType)) {
              invalidSupports.push(
                `Row ${rowNum}: Invalid type '${support.type}' (must be: fixed, pinned, or roller)`
              );
              return;
            }

            const validPositions: SupportPosition[] = [
              "below",
              "above",
              "left",
              "right",
              "front",
              "back",
            ];
            if (!validPositions.includes(support.position as SupportPosition)) {
              invalidSupports.push(
                `Row ${rowNum}: Invalid position '${support.position}' (must be: below, above, left, right, front, back)`
              );
              return;
            }

            let cleanNodeId = "";
            if (support.nodeId && typeof support.nodeId === "string") {
              cleanNodeId = support.nodeId.trim();
            } else if (support.nodeId && typeof support.nodeId === "number") {
              cleanNodeId = String(support.nodeId);
            }

            if (cleanNodeId) {
              const nodeExists = nodes.find((n) => n.id === cleanNodeId);
              if (!nodeExists) {
                missingNodeIds.add(cleanNodeId);
                console.warn(
                  `⚠️ Row ${rowNum}: Node '${cleanNodeId}' not found`
                );
              }
            }

            const parseDOF = (): DegreesOfFreedom | undefined => {
              if (support.dof && typeof support.dof === "object") {
                return {
                  Fx: Number(support.dof.Fx ?? 0),
                  Fy: Number(support.dof.Fy ?? 0),
                  Fz: Number(support.dof.Fz ?? 0),
                  Rx: Number(support.dof.Rx ?? 0),
                  Ry: Number(support.dof.Ry ?? 0),
                  Rz: Number(support.dof.Rz ?? 0),
                };
              }
              return undefined;
            };

            validSupports.push({
              ...support,
              nodeId: cleanNodeId, // Keep singular for now
              type: support.type as SupportType,
              position: support.position as SupportPosition,
              dof: parseDOF(),
            });
          });

          if (invalidSupports.length > 0) {
            alert(
              `Failed to import supports:\n\n${invalidSupports.join("\n")}\n\n` +
                `Valid supports found: ${validSupports.length}`
            );
            return;
          }

          // GROUP BY SUPPORT ID: Convert nodeId to nodeIds array
          const supportMap = new Map<string, any>();

          validSupports.forEach((support: any) => {
            const supportType = support.type as SupportType;

            // Use existing ID or generate new one
            const supportId =
              support.id ||
              `support_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

            if (supportMap.has(supportId)) {
              // Support already exists - add this node to nodeIds array
              const existing = supportMap.get(supportId);
              if (
                support.nodeId &&
                !existing.nodeIds.includes(support.nodeId)
              ) {
                existing.nodeIds.push(support.nodeId);
              }
            } else {
              // New support - create with nodeIds array
              supportMap.set(supportId, {
                id: supportId,
                nodeIds: support.nodeId ? [support.nodeId] : [], //  Convert to array
                type: supportType,
                position: support.position,
                dof: support.dof || DEFAULT_DOF_CONFIGS[supportType],
                color:
                  support.color ||
                  SUPPORT_VISUAL_CONFIGS[supportType]?.color ||
                  "#00ff00",
                opacity: support.opacity ?? 0.6,
                size: support.size ?? 1.0,
                offset: support.offset ?? 0.1,
              });
            }
          });

          // Convert map to array
          const enhancedSupports = Array.from(supportMap.values());

          let confirmMessage = `Import ${enhancedSupports.length} supports (${validSupports.length} node assignments)?\n\nThis will replace existing supports. Continue?`;

          if (missingNodeIds.size > 0) {
            confirmMessage += `\n\n⚠️ Note: ${missingNodeIds.size} node(s) not found:\n${Array.from(missingNodeIds).join(", ")}`;
          }

          const confirmReplace = window.confirm(confirmMessage);

          if (confirmReplace) {
            if (setSupports) {
              setSupports(enhancedSupports);

              let successMessage = `✅ Successfully imported ${enhancedSupports.length} supports with ${validSupports.length} node assignments!`;

              if (missingNodeIds.size > 0) {
                successMessage += `\n\n📌 ${missingNodeIds.size} nodes not found yet:\n${Array.from(missingNodeIds).join(", ")}`;
              }

              alert(successMessage);
            } else {
              alert("Import functionality is not available.");
            }
          }
        } else {
          alert(`Failed to import supports:\n${result.errors.join("\n")}`);
        }
      } catch (error) {
        console.error("File processing error:", error);
        alert(
          `An error occurred while processing the file:\n${error instanceof Error ? error.message : "Unknown error"}\n\nPlease check the file format and try again.`
        );
      }

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [setSupports, nodes]
  );

  const handleCopy = useCallback(async () => {
    const success = await copySupportsToClipboard(supports);
    if (success) {
      alert("Supports copied to clipboard!");
    } else {
      alert("Failed to copy to clipboard. Please try again.");
    }
  }, [supports]);

  const theader: TableColumn[] = [
    {
      colname: "ID",
      field: "supportId",
      rowSpan: 2,
      className: "w-[50px] text-center",
    },
    {
      colname: "Node(s)",
      render: (row) => {
        // If editing this support's nodes
        if (editingNodeIds?.supportId === row.id) {
          return (
            <input
              type="text"
              autoFocus
              className="w-full h-8 px-2 border rounded text-black text-sm"
              value={editingNodeIds.value}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) =>
                setEditingNodeIds((prev) =>
                  prev ? { ...prev, value: e.target.value } : prev
                )
              }
              onBlur={commitNodeIdsEdit}
              onKeyDown={(e) => {
                if (e.key === "Enter") commitNodeIdsEdit();
                if (e.key === "Escape") setEditingNodeIds(null);
              }}
              placeholder="e.g. 1, 2, 3"
            />
          );
        }

        // Display mode - click to edit (including empty state)
        const displayText =
          row.nodeIds && row.nodeIds.length > 0
            ? row.nodeIds.join(", ")
            : "(none)";

        return (
          <span
            className="inline-flex items-center h-8 w-full px-2 bg-gray-50 rounded cursor-text text-black text-sm"
            onClick={(e) => startEditingNodes(e, row.id, row.nodeIds || [])}
          >
            {displayText}
          </span>
        );
      },
      rowSpan: 2,
      className: "min-w-[100px]",
    },
    {
      colname: "Type",
      rowSpan: 2,
      render: (row) => (
        <span
          className={`px-1.5 py-0.5 rounded text-white ${
            row.type === "fixed"
              ? "bg-green-500"
              : row.type === "pinned"
                ? "bg-yellow-500"
                : "bg-red-500"
          }`}
        >
          {row.type}
        </span>
      ),
    },
    {
      colname: "Position",
      rowSpan: 2,
      render: (row) => SUPPORT_POSITION_LABELS[row.position] || row.position,
    },
    {
      colname: "Fx",
      render: (row) => row.dof?.Fx ?? "-",
      className: "w-[60px] text-center",
    },
    {
      colname: "Fy",
      render: (row) => row.dof?.Fy ?? "-",
      className: "w-[60px] text-center",
    },
    {
      colname: "Fz",
      render: (row) => row.dof?.Fz ?? "-",
      className: "w-[60px] text-center",
    },
    {
      colname: "Rx",
      render: (row) => row.dof?.Rx ?? "-",
      className: "w-[60px] text-center",
    },
    {
      colname: "Ry",
      render: (row) => row.dof?.Ry ?? "-",
      className: "w-[60px] text-center",
    },
    {
      colname: "Rz",
      render: (row) => row.dof?.Rz ?? "-",
      className: "w-[60px] text-center",
    },
    {
      colname: "Actions",
      rowSpan: 2,
      className: "w-[90px] text-center",
      render: (row) => (
        <div className="flex gap-1 justify-center">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(row);
            }}
            className="p-1 rounded hover:bg-red-100 text-red-600"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ),
    },
  ];

  const footerUI = (
    <>
      {/* Support selected */}
      {selectedSupport && !applyByClickMode && !applyToSelectedMode && (
        <div className="flex gap-2 justify-center">
          <button
            onClick={() => {
              unselectRow();
              clearCursorMode();
              setApplyByClickMode(false);
              setApplyToSelectedMode(false);
              setActivationMessage("Selection canceled.");
              setTimeout(() => setActivationMessage(null), 1000);
            }}
            className="px-3 py-1.5 text-xs font-medium bg-red-500 text-white rounded hover:bg-red-600"
          >
            Cancel
          </button>

          <button
            onClick={() => {
              const model = useModelStore.getState();
              if (model.clearSelection) model.clearSelection();

              setApplyByClickMode(true);

              setCursorMode("support-apply-click", selectedSupport);
            }}
            className="px-3 py-1.5 text-xs font-medium bg-blue-500 text-white rounded"
          >
            Apply by Click
          </button>

          <button
            disabled={selectedNodeIds.length === 0}
            onClick={() => {
              setApplyToSelectedMode(true);
              setActivationMessage(
                `Apply to Selected active → ${selectedNodeIds.length} node(s) will be updated`
              );
            }}
            className="px-3 py-1.5 text-xs font-medium bg-purple-500 text-white rounded disabled:opacity-50"
          >
            Apply to Selected
          </button>

          <button
            onClick={() => {
              setManualPopupOpen(true);
            }}
            className="px-3 py-1.5 text-xs font-medium bg-green-500 text-white rounded"
          >
            Manual Input
          </button>
        </div>
      )}

      {applyByClickMode && (
        <div className="flex gap-2 justify-center">
          <button
            onClick={() => {
              setApplyByClickMode(false);
              unselectRow();
              clearCursorMode();
              setActivationMessage("Apply by Click canceled.");
              setTimeout(() => {
                setActivationMessage(null);
              }, 1000);
            }}
            className="px-3 py-1.5 text-xs font-medium bg-red-500 text-white rounded"
          >
            Cancel
          </button>

          <span className="px-3 py-1.5 text-xs text-blue-600">
            Active: {selectedSupport?.type} at {selectedSupport?.position}
          </span>
        </div>
      )}

      {applyToSelectedMode && (
        <div className="flex gap-2 justify-center">
          <button
            onClick={() => {
              setApplyToSelectedMode(false);
              unselectRow();
              clearCursorMode();
              setActivationMessage("Apply to Selected canceled.");
              setTimeout(() => {
                setActivationMessage(null);
              }, 1000);
            }}
            className="px-3 py-1.5 text-xs font-medium bg-red-500 text-white rounded"
          >
            Cancel
          </button>

          <button
            onClick={handleApplyToSelectedConfirm}
            className="px-3 py-1.5 text-xs font-medium bg-purple-500 text-white rounded"
          >
            Apply ({selectedNodeIds.length} nodes)
          </button>
        </div>
      )}
    </>
  );

  return (
    <>
      <TableLayout
        title="Supports"
        theader={theader}
        tdata={filteredAndIndexed}
        onRowClick={handleRowToggle}
        getRowClassName={(row) => {
          const isSelected =
            globalActiveTable === "supports" && globalActiveRowId === row.id;
          return isSelected
            ? "selected-table-row bg-blue-50 dark:bg-gray-700"
            : "";
        }}
        filters={
          <div className="px-2 py-2 flex gap-2 items-center bg-gray-50 dark:bg-gray-900 border-b">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as any)}
              className="w-[250px] px-2 py-1.5 text-xs rounded border border-gray-300 bg-white text-gray-800"
            >
              <option value="all">All Supports</option>
              <option value="fixed">Fixed</option>
              <option value="pinned">Pinned</option>
              <option value="roller">Roller</option>
            </select>

            <button
              onClick={() => fileInputRef.current?.click()}
              className="p-1 hover:bg-gray-600 rounded"
              title="Import from CSV"
            >
              <ArrowDownOnSquareIcon className="h-5 w-5" />
            </button>

            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelected}
              accept=".csv,.xlsx,.xls"
              style={{ display: "none" }}
            />

            <button
              onClick={handleExport}
              className="p-1 hover:bg-gray-600 rounded"
              title="Export to CSV"
            >
              <ArrowUpOnSquareIcon className="h-5 w-5" />
            </button>

            <button
              onClick={handleCopy}
              className="p-1 hover:bg-gray-600 rounded"
              title="Copy to Clipboard"
            >
              <ClipboardDocumentIcon className="h-5 w-5" />
            </button>

            <button
              onClick={() => setSupportDialogVisible(true)}
              className="px-3 py-1.5 text-xs rounded bg-blue-500 text-white flex items-center gap-1 hover:bg-blue-600"
            >
              <Plus className="h-4 w-4" /> Add
            </button>

            <button
              onClick={toggleVisible}
              className="ml-auto p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
            >
              {isVisible ? (
                <Eye className="w-4 h-4 text-blue-500" />
              ) : (
                <EyeOff className="w-4 h-4 text-gray-400" />
              )}
            </button>
          </div>
        }
        extra={
          globalActiveTable === "supports" && selectedSupport ? footerUI : null
        }
      />

      {activationMessage && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 bg-blue-600 text-white px-6 py-3 rounded-lg shadow-xl">
          <p className="text-sm font-medium">{activationMessage}</p>
        </div>
      )}

      {/* Support Create Dialog */}
      <SupportConfigDialog
        visible={supportDialogVisible}
        nodeId={null}
        onConfirm={handleSupportConfirm}
        onCancel={() => setSupportDialogVisible(false)}
      />

      {manualPopupOpen && selectedSupport && (
        <ManualLoadInput
          onClose={() => setManualPopupOpen(false)}
          onApply={(ids) => applyManualSupport(ids)}
          mode="node"
          loadName={
            selectedSupport?.type + " (" + selectedSupport?.position + ")"
          }
          existingIds={selectedSupport?.nodeIds || []}
        />
      )}
    </>
  );
};
